import { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from '@scholastic/mockingjay';
import { useGetMeApi } from 'eds-common/api/v2';
import { useGetClassesApi } from 'eds-common/api/v3';
import {
  GOOGLE_IMPORT_SOURCES,
  type GoogleCourse,
  useGetGoogleCoursesApi,
} from '../../hooks/api/use-get-google-courses-api/use-get-google-courses-api';
import { GoogleClassroomClassList } from '../GoogleClassroomClassList/GoogleClassroomClassList';

type ImportGoogleClassesContentVariant = 'drawer' | 'dialog';

interface ImportGoogleClassesContentProps {
  open: boolean;
  userName: string;
  onCancel: () => void;
  onImport: (selectedClasses: GoogleCourse[]) => Promise<void> | void;
  classes?: GoogleCourse[];
  isImporting?: boolean;
  variant?: ImportGoogleClassesContentVariant;
  showIntroText?: boolean;
}

const BUTTON_PADDING_CLASS_NAME = 'py-2.5';
const DRAWER_CONTENT_BASE_CLASS_NAME =
  'text-neutral-1100 flex h-[calc(100dvh-9rem)] max-h-[calc(100dvh-9rem)] min-h-0 flex-col overflow-hidden tablet:h-[calc(100dvh-8rem)] tablet:max-h-[calc(100dvh-8rem)]';

interface VariantConfig {
  actionClassName: string;
  cancelLabel: string;
  classListClassName: string;
  classesSectionClassName: string;
  classesSelectedLabelClassName: string;
  classesSelectedValueClassName: string;
  contentClassName: string;
  countRowClassName: string;
  disableImportWhenNoSelection: boolean;
  emptyContentClassName: string;
  importLabel: string;
  importLabelClassName: string;
  listClassName: string;
  selectAllLabel: string;
  subtitleClassName: string;
  subtitleTitle: string;
}

const BASE_VARIANT_CONFIG = {
  classesSectionClassName: 'flex flex-col gap-4',
  classesSelectedLabelClassName: 'text-sm font-medium leading-6 text-[#191918]',
  classesSelectedValueClassName:
    'w-[99px] text-base font-bold leading-6 text-[#191918]',
  countRowClassName: 'flex items-center gap-3',
} as const;

const VARIANT_CONFIG = {
  drawer: {
    ...BASE_VARIANT_CONFIG,
    actionClassName: 'flex gap-2',
    cancelLabel: 'Cancel',
    classListClassName:
      'w-full max-w-[800px] max-h-[calc(100dvh-25rem)] overflow-hidden pr-[2px] laptop:max-h-[calc(100dvh-22rem)]',
    contentClassName: `${DRAWER_CONTENT_BASE_CLASS_NAME} px-6 py-2`,
    disableImportWhenNoSelection: true,
    emptyContentClassName: `${DRAWER_CONTENT_BASE_CLASS_NAME} px-10 py-6`,
    importLabel: 'Import',
    importLabelClassName: `${BUTTON_PADDING_CLASS_NAME} disabled:!bg-[#E8E8E8] disabled:!text-[#6D6D6D]`,
    listClassName: 'pr-2',
    selectAllLabel: 'Select all',
    subtitleClassName: 'text-lg font-medium leading-7 text-[#191918]',
    subtitleTitle: 'Select your classes',
  },
  dialog: {
    ...BASE_VARIANT_CONFIG,
    actionClassName: 'flex w-full justify-end gap-2',
    cancelLabel: 'Skip',
    classListClassName: 'w-full py-2 pr-[2px]',
    contentClassName: 'text-neutral-1100 flex flex-col gap-4',
    disableImportWhenNoSelection: false,
    emptyContentClassName: 'text-neutral-1100 flex flex-col gap-8 px-8 py-8',
    importLabel: 'Import classes',
    importLabelClassName: BUTTON_PADDING_CLASS_NAME,
    listClassName: 'max-h-[235px] pr-1 pl-1',
    selectAllLabel: 'All Classes',
    subtitleClassName: 'text-base font-medium leading-6 text-[#4D4D4D]',
    subtitleTitle: '',
  },
} as const satisfies Record<ImportGoogleClassesContentVariant, VariantConfig>;

function ImportGoogleClassesEmptyState() {
  return (
    <div className="w-full rounded-sm border border-dashed border-[#A9A9A9] p-10">
      <div className="flex flex-col gap-2 text-[#333333]">
        <p className="text-lg font-bold leading-7">No classes to import</p>
        <p className="max-w-[650px] text-sm font-medium leading-6">
          You don&apos;t have any classes in your Google Classroom. Once you
          create classes there, you can import them here.
        </p>
      </div>
    </div>
  );
}

function ImportGoogleClassesLoadingState() {
  return (
    <div
      className="w-fit rounded-sm bg-white py-2 pl-4 pr-0.5"
      data-testid="google-class-import-loading-state"
    >
      <Spinner
        size="xs"
        label="Loading classes..."
        className="[&>div]:gap-1 [&_span]:text-sm [&_span]:font-medium [&_span]:leading-[1.5] [&_span]:text-black"
      />
    </div>
  );
}

function GoogleImportErrorIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="size-4 shrink-0"
      fill="none"
    >
      <path
        d="M10.0001 18.3334C12.2102 18.3334 14.3298 17.4554 15.8926 15.8926C17.4554 14.3298 18.3334 12.2102 18.3334 10C18.3334 7.78988 17.4554 5.67027 15.8926 4.10746C14.3298 2.54466 12.2102 1.66669 10.0001 1.66669C7.78994 1.66669 5.67033 2.54466 4.10753 4.10746C2.54472 5.67027 1.66675 7.78988 1.66675 10C1.66675 12.2102 2.54472 14.3298 4.10753 15.8926C5.67033 17.4554 7.78994 18.3334 10.0001 18.3334ZM10.0001 5.83335C10.433 5.83335 10.7813 6.18166 10.7813 6.6146V10.2604C10.7813 10.6934 10.433 11.0417 10.0001 11.0417C9.56714 11.0417 9.21883 10.6934 9.21883 10.2604V6.6146C9.21883 6.18166 9.56714 5.83335 10.0001 5.83335ZM8.95842 13.125C8.95842 12.8488 9.06816 12.5838 9.26351 12.3885C9.45886 12.1931 9.72381 12.0834 10.0001 12.0834C10.2763 12.0834 10.5413 12.1931 10.7367 12.3885C10.932 12.5838 11.0417 12.8488 11.0417 13.125C11.0417 13.4013 10.932 13.6662 10.7367 13.8616C10.5413 14.0569 10.2763 14.1667 10.0001 14.1667C9.72381 14.1667 9.45886 14.0569 9.26351 13.8616C9.06816 13.6662 8.95842 13.4013 8.95842 13.125Z"
        fill="#DB0C0C"
      />
    </svg>
  );
}

function ImportGoogleClassesErrorState() {
  return (
    <div
      className="w-fit rounded-sm bg-white py-2 pl-4 pr-0.5"
      data-testid="google-class-import-error-state"
      role="alert"
    >
      <p className="flex items-center gap-1 text-sm font-medium leading-[1.5] text-black">
        <GoogleImportErrorIcon />
        Unable to load your classes. Please try again.
      </p>
    </div>
  );
}

function ImportGoogleClassesIntro({
  config,
  hasImportedGoogleClasses,
}: {
  config: VariantConfig;
  hasImportedGoogleClasses: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {config.subtitleTitle ? (
        <p className="text-xl font-bold leading-none">{config.subtitleTitle}</p>
      ) : null}
      <p className={config.subtitleClassName}>
        By importing a class, you will give those students access to the
        Scholastic Education Platform.
      </p>
      {hasImportedGoogleClasses ? (
        <p className={config.subtitleClassName}>
          If you do not see your class, it may have already been imported.
        </p>
      ) : null}
    </div>
  );
}

function useAvailableGoogleCourses(
  open: boolean,
  unfilteredClasses: GoogleCourse[]
) {
  const { data: me } = useGetMeApi();
  const { data: existingClasses = [] } = useGetClassesApi(
    {
      userId: me?.userId ?? '',
      orgId: me?.currentOrg?.orgId ?? '',
      source: me?.userSource ?? 'GoogleClassroom',
    },
    { enabled: open && Boolean(me?.userId && me?.currentOrg?.orgId) }
  );

  const importedGoogleCourseIds = useMemo(
    () =>
      new Set(
        existingClasses
          .filter((c) => c.source_system === 'GoogleClassroom' && c.source_id)
          .map((c) => c.source_id as string)
      ),
    [existingClasses]
  );

  const availableClasses = useMemo(
    () =>
      unfilteredClasses.filter(
        (gc) => !importedGoogleCourseIds.has(gc.course_id)
      ),
    [unfilteredClasses, importedGoogleCourseIds]
  );

  return {
    availableClasses,
    hasImportedGoogleClasses: importedGoogleCourseIds.size > 0,
  };
}

export function ImportGoogleClassesContent({
  open,
  userName,
  onCancel,
  onImport,
  classes,
  isImporting = false,
  variant = 'drawer',
  showIntroText = true,
}: ImportGoogleClassesContentProps) {
  const shouldFetchGoogleCourses = open && classes === undefined;
  const {
    data: fetchedGoogleCourses = [],
    isLoading: isGoogleCoursesLoading,
    isError: isGoogleCoursesError,
    isRedirectingToConsent,
  } = useGetGoogleCoursesApi(
    shouldFetchGoogleCourses,
    variant === 'dialog'
      ? GOOGLE_IMPORT_SOURCES.dialog
      : GOOGLE_IMPORT_SOURCES.drawer
  );

  const unfilteredClasses = classes ?? fetchedGoogleCourses;
  const { availableClasses, hasImportedGoogleClasses } =
    useAvailableGoogleCourses(open, unfilteredClasses);

  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);

  const config = VARIANT_CONFIG[variant];
  const hasClasses = availableClasses.length > 0;
  const isGoogleCoursesPending =
    isGoogleCoursesLoading || isRedirectingToConsent;
  const showEmptyState =
    !isGoogleCoursesPending && !isGoogleCoursesError && !hasClasses;
  const contentClassName = showEmptyState
    ? config.emptyContentClassName
    : config.contentClassName;

  useEffect(() => {
    if (!open) {
      setSelectedClassIds([]);
    }
  }, [open]);

  const selectedCount = selectedClassIds.length;
  const totalClasses = availableClasses.length;

  const isImportDisabled =
    isImporting ||
    isGoogleCoursesPending ||
    !hasClasses ||
    (config.disableImportWhenNoSelection && selectedClassIds.length === 0);

  const resetSelection = () => {
    setSelectedClassIds([]);
  };

  const handleCancel = () => {
    resetSelection();
    onCancel();
  };

  const handleImport = () => {
    void onImport(
      availableClasses.filter((c) => selectedClassIds.includes(c.course_id))
    );
  };

  const intro = (
    <ImportGoogleClassesIntro
      config={config}
      hasImportedGoogleClasses={hasImportedGoogleClasses}
    />
  );

  const actions = (
    <div className={config.actionClassName}>
      <Button
        variant="secondary"
        className={BUTTON_PADDING_CLASS_NAME}
        disabled={isImporting}
        onClick={handleCancel}
      >
        {config.cancelLabel}
      </Button>
      <Button
        variant="primary"
        className={config.importLabelClassName}
        disabled={isImportDisabled}
        onClick={handleImport}
      >
        {config.importLabel}
      </Button>
    </div>
  );

  let contentBody = <ImportGoogleClassesEmptyState />;

  if (isGoogleCoursesPending) {
    contentBody = <ImportGoogleClassesLoadingState />;
  } else if (isGoogleCoursesError) {
    contentBody = <ImportGoogleClassesErrorState />;
  } else if (hasClasses) {
    contentBody = (
      <div className={config.classesSectionClassName}>
        <GoogleClassroomClassList
          classes={availableClasses}
          userName={userName}
          selectedClassIds={selectedClassIds}
          onSelectionChange={setSelectedClassIds}
          selectAllLabel={config.selectAllLabel}
          className={config.classListClassName}
          listClassName={config.listClassName}
        />

        <p
          className={config.countRowClassName}
          data-testid="selected-class-count"
        >
          <span className={config.classesSelectedLabelClassName}>
            Classes selected
          </span>
          <span className={config.classesSelectedValueClassName}>
            {selectedCount}/{totalClasses}
          </span>
        </p>
      </div>
    );
  }

  if (variant === 'dialog') {
    return (
      <div className="flex w-full flex-col">
        <div className={contentClassName}>
          {showIntroText ? intro : null}
          {contentBody}
        </div>
        <div className="flex w-full items-end">{actions}</div>
      </div>
    );
  }

  return (
    <div className={contentClassName}>
      {intro}
      <div className="tablet:mt-6 tablet:gap-6 mt-4 flex min-h-0 flex-1 flex-col gap-4">
        {contentBody}
        <div className="shrink-0">{actions}</div>
      </div>
    </div>
  );
}

export type {
  ImportGoogleClassesContentVariant,
  ImportGoogleClassesContentProps,
};
